import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  DataListItem,
  Highlight,
  Icon,
  RowsSkeletonLoader,
  Tooltip,
} from '@node-space/ui-components'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useGetAssociates } from 'hooks/queries/Customers/useAssociatesQuery'
import { DEFAULT_DOC_ICON, DOC_TYPE_ICON } from 'pages/EmbeddedCustomers/helpers/constants'
import { CustomerApplicationDetails } from 'types/customers'
import {
  ASSOCIATE_TYPE_TRANSLATION_KEYS,
  REQUIRED_ACTION_CATEGORY_ASSOCIATES,
  REQUIRED_ACTION_CODE_ALL_ASSOCIATES_VALID,
  REQUIRED_ACTION_CODE_ASSOCIATE_INFO_REQUIRED,
} from '../helpers/constants'

interface CustomerAssociatesTabProps {
  associates: CustomerApplicationDetails
  isV1CustomerOnboarding?: boolean
  customerReference: string
}

export const CustomerAssociatesTab = ({
  associates,
  customerReference,
  isV1CustomerOnboarding,
}: CustomerAssociatesTabProps) => {
  const { t } = useTranslation()

  const missingRoles =
    associates?.requiredActions
      ?.filter(
        ({ category, code }) =>
          category === REQUIRED_ACTION_CATEGORY_ASSOCIATES &&
          code !== REQUIRED_ACTION_CODE_ALL_ASSOCIATES_VALID
      )
      .map(({ code, description }) =>
        isV1CustomerOnboarding && code === REQUIRED_ACTION_CODE_ASSOCIATE_INFO_REQUIRED
          ? {
              key: description,
              label: t(ASSOCIATE_TYPE_TRANSLATION_KEYS[description] ?? description),
            }
          : { key: code, label: t(ASSOCIATE_TYPE_TRANSLATION_KEYS[code] ?? code) }
      ) ?? []

  const {
    data: associateData,
    isLoading: isLoadingAssociates,
    isError: isAssociateError,
    refetch,
  } = useGetAssociates(customerReference)

  if (isLoadingAssociates) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (isAssociateError) {
    return (
      <Box paddingX={16} paddingY={32}>
        <RequestErrorStatusCard onReload={refetch} />
      </Box>
    )
  }

  const propAssociatesById = new Map((associates?.company?.associates ?? []).map(a => [a.id, a]))
  const existingAssociates = (associateData?.content ?? []).filter(a =>
    propAssociatesById.has(a.id)
  )
  const showMissingRoles = !(isV1CustomerOnboarding && existingAssociates.length > 0)

  return (
    <Box flexCol paddingY={16}>
      {showMissingRoles &&
        missingRoles.map(({ key, label }) => (
          <DataListItem
            key={key}
            id={key}
            layout="horizontal"
            title={label}
            body=""
            testId="customer-associate-missing-role"
            HeaderHighlight={
              <Highlight type="warning">{t('embeddedCustomers.status.infoRequired')}</Highlight>
            }
          />
        ))}

      {existingAssociates.map(associate => {
        const isComplete = associate.validation?.status === 'COMPLETE'
        const propAssociate = propAssociatesById.get(associate.id)
        const allDocs = [
          ...(propAssociate?.entity?.identificationDocuments ?? []).map(d => d.type),
          ...(propAssociate?.entity?.proofOfAddresses ?? []).map(d => d.documentType),
        ]
        return (
          <Box key={associate.id} flexCol className="gap-1">
            <DataListItem
              id={associate.id}
              layout="vertical"
              title={associate.types
                .map(type => t(ASSOCIATE_TYPE_TRANSLATION_KEYS[type] ?? type))
                .join(' / ')}
              body={associate.displayName}
              testId="customer-associate"
              HeaderHighlight={
                <Highlight type={isComplete ? 'success' : 'warning'}>
                  {isComplete
                    ? t('embeddedCustomers.status.verified')
                    : t('embeddedCustomers.status.infoRequired')}
                </Highlight>
              }
            />
            {!isComplete && !!allDocs.length && (
              <Box testId="associate-doc-icons" className="flex flex-row gap-3 pb-2 justify-end">
                {allDocs.map((docType, idx) => {
                  const mapping = DOC_TYPE_ICON[docType]
                  const iconName = mapping?.icon ?? DEFAULT_DOC_ICON
                  const label = mapping ? t(mapping.labelKey, { defaultValue: docType }) : docType
                  return (
                    <Tooltip key={`${docType}-${idx}`} bodyContent={label}>
                      <Box className="text-foreground-subdued hover:text-foreground-prominent transition-colors cursor-default">
                        <Icon name={iconName} size="medium" />
                      </Box>
                    </Tooltip>
                  )
                })}
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
